import { DemoBanner } from './DemoBanner'
import { ReadinessPanel } from './Readiness'
import { onSessionExpired, type Readiness } from './api'
import { setUnitSystem } from './units'
import { GeoAreaEditor } from './GeoAreas'
import { Tabs } from './Tabs'
import { RevisionPanel } from './Revision'
import { setShiftTemplates as setAgencyShifts } from './shifts'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { HubConnection } from '@microsoft/signalr'
import {
  type District,
  type ShiftTemplate,
  AdminApi, AtlasApi, capabilities, CommandApi, DeniedError, InsightApi,
  me as quienSoy, myBranding, PRODUCT_BRANDING, StrategyApi, TactixApi, DEFAULT_MAP_BASE,
  type MapArea, type MapBase,
  type AuditEntry, type AvailabilityEntry, type Device, type Evaluation, type FormUnit,
  type ApprovalPolicyView, type DeadLetterSummary, type DeadLetterItem, type PendingRevision, type FormationPolicyView, type BackfillResult, type DeviationPolicyView,
  type IncidentFilter, type IncidentPage,
  type ProfileDraft, type QualityReport, type ShiftPerformance, type ShiftReview,
  type StorageView, type StrategyProfile,
  type Trends,
  type GenerateScenario, type Jurisdiction, type StaffingTarget,
  type Handover, type HandoverSnapshot, type ProfileComparison, type ShiftNow, type Timeline,
  type HotspotSet, type HotspotSetDetail, type OfficerProfile, type SetChanges, type Vehicle,
  type VehicleHistory,
  type IngestionRun, type Scenario, type ShiftRoster, type ShiftSummary, type Source,
  type Alert, type AlertAction, type AssignmentView, type Connection, type Denial, type Incident, type IncidentAction,
  type Branding, type LicenseView, type Simulation, type SimulationRequest, type UnitView,
} from './api'
import { denialText, initialLang, translate, type Lang } from './i18n'
import { connectRealtime, stopRealtime, type LinkState } from './realtime'
import { LiveMap, type AlertPoint } from './LiveMap'
import { matchesQuery, nearbyAlertIds, samePlaceAlertIds, sortAlerts, withinBounds, type Bounds, type SortMode } from './queue'
import { completeSignIn, createSignIn, type OidcSettings } from './auth'
import type { LoadKey } from './state'
import type { UserManager } from 'oidc-client-ts'
import { AlertsPanel, DemoPanel, IncidentsPanel, LicenseBanner, ReplanPanel, UnitsPanel } from './panels'
import { AnalystScreen } from './Analyst'
import { PlannerScreen } from './Planner'
import { FleetScreen, HrScreen } from './Resources'
import { AdminScreen } from './Admin'
import { HandoverPanel } from './Handover'
import { TimelinePanel } from './Timeline'
import { InsightScreen } from './Insight'
import { StrategyScreen } from './Strategy'
import { AgencyBadge, badgeOf, SignIn } from './SignIn'
import { appPalette } from './theme'
import { agencyToday, setAgencyZone, setTimeMode, timeMode, useTimeVersion, zoneLabel, zonesDiffer } from './time'

/** Cada cuánto se vuelve a consultar todo aunque no llegue ningún aviso (red de seguridad). */
const REFRESH_MS = 20_000

/** Radio para considerar dos alertas contiguas: unas cuadras, no un barrio entero. */
const NEARBY_METERS = 500

/**
 * Las áreas de §16.3 que ya existen, con las capacidades que abren cada una.
 *
 * Son varias por área porque una misma pantalla la usan roles distintos con permisos distintos:
 * al área de planificación entra quien genera escenarios y también quien solo los aprueba.
 */
type Area = 'command' | 'analyst' | 'planner' | 'fleet' | 'hr' | 'strategy' | 'insight' | 'admin'

const AREAS: { id: Area; capabilities: string[] }[] = [
  { id: 'command', capabilities: ['shift.monitor'] },
  { id: 'planner', capabilities: ['plan.create', 'plan.propose', 'plan.approve'] },
  { id: 'analyst', capabilities: ['hotspots.read'] },
  { id: 'fleet', capabilities: ['fleet.manage'] },
  { id: 'hr', capabilities: ['officers.manage'] },
  { id: 'strategy', capabilities: ['strategy.read'] },
  { id: 'insight', capabilities: ['reports.read'] },
  { id: 'admin', capabilities: ['devices.manage', 'users.manage', 'audit.read', 'agency.configure', 'agency.rules'] },
]

/** Las áreas donde se prepara el turno: en ellas se muestra qué le toca a cada rol (H-68). */
const PREPARAN = new Set<string>(['hr', 'fleet', 'analyst', 'strategy', 'planner'])

export default function App() {
  // Se vuelve a dibujar cuando cambia la zona de la agencia o el modo de hora (H-01).
  useTimeVersion()
  const [lang, setLang] = useState<Lang>(initialLang)
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem('theme')
    return saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches
  })
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem('token'))
  const [meTry, setMeTry] = useState(0)
  const [connection, setConnection] = useState<Connection>(() => ({
    server: window.location.origin,
    tenantId: localStorage.getItem('tenant') ?? 'i4ds-demo',
    agencyId: localStorage.getItem('agency') ?? 'US-TX-FORT-WORTH-PD',
    user: '',
    roles: [],
  }))

  const tokenRef = useRef(token)
  tokenRef.current = token
  // La ventana de la bitácora, por referencia: la recarga periódica la lee sin volver a armarse
  // cada vez que alguien la cambia.
  const hoursRef = useRef(12)
  const demoRef = useRef(false)
  const api = useMemo(() => new CommandApi(() => tokenRef.current), [])
  const atlas = useMemo(() => new AtlasApi(() => tokenRef.current), [])
  const tactix = useMemo(() => new TactixApi(() => tokenRef.current), [])
  const adminApi = useMemo(() => new AdminApi(() => tokenRef.current), [])
  const insight = useMemo(() => new InsightApi(() => tokenRef.current), [])
  const strategy = useMemo(() => new StrategyApi(() => tokenRef.current), [])

  const [units, setUnits] = useState<UnitView[]>([])
  const [assignments, setAssignments] = useState<AssignmentView[]>([])
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [showAllAlerts, setShowAllAlerts] = useState(false)
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<SortMode>('severity')
  const [onlyVisible, setOnlyVisible] = useState(false)
  const [nearbyOnly, setNearbyOnly] = useState(false)
  const [samePlaceOnly, setSamePlaceOnly] = useState(false)
  const [bounds, setBounds] = useState<Bounds | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [selectedAlert, setSelectedAlert] = useState<string | null>(null)
  const [selectedIncident, setSelectedIncident] = useState<string | null>(null)
  // La pestaña de la columna del Centro de Mando. Se maneja desde aquí porque elegir algo en el
  // mapa lleva a la pestaña donde está su tarjeta.
  const [commandTab, setCommandTab] = useState<string>(() => {
    try { return localStorage.getItem('tab:command') ?? 'now' } catch { return 'now' }
  })
  const [link, setLink] = useState<LinkState>('connecting')
  const [problem, setProblem] = useState<Denial | null>(null)
  // La sesión vencida lleva al ingreso desde cualquier pantalla, no solo desde las que usan guard,
  // y el ingreso dice por qué: sin eso la persona volvía a la portada sin explicación.
  useEffect(() => {
    onSessionExpired(() => {
      sessionStorage.removeItem('token')
      setToken(null)
      setProblem({ code: 'app.session_expired', reason: '' })
    })
    return () => onSessionExpired(null)
  }, [])
  /** Qué áreas ya trajeron datos alguna vez; ver `guard` y §13. */
  const [loaded, setLoaded] = useState<ReadonlySet<LoadKey>>(new Set())
  /** Qué carga falló la última vez que se intentó, por separado del rechazo global. */
  const [failures, setFailures] = useState<ReadonlyMap<LoadKey, Denial>>(new Map())

  // Al salir, las marcas se olvidan. Si no, la sesión siguiente empezaría creyendo que ya sabe
  // que no hay nada —el estado quedó de la anterior— y volvería a afirmar «no hay ninguno» antes
  // de haber pedido nada, que es exactamente el defecto que esto arregla.
  useEffect(() => { if (!token) { setLoaded(new Set()); setFailures(new Map()) } }, [token])
  const [busy, setBusy] = useState(false)
  /** Vuelve a leer la guía del turno; lo fija el efecto que la carga. */
  const refrescarGuia = useRef<(() => void) | null>(null)
  const puedeVigilarRef = useRef(false)
  const [replanResult, setReplanResult] = useState<string | null>(null)
  const [formResult, setFormResult] = useState<string | null>(null)
  const [demo, setDemo] = useState(false)
  const [storage, setStorage] = useState<StorageView>({ encryptionAtRest: 'undeclared', encrypted: false })
  const [mfa, setMfa] = useState<'required' | 'provider' | 'off' | null>(null)
  const [mapBase, setMapBase] = useState<MapBase>(DEFAULT_MAP_BASE)
  const [agencyArea, setAgencyArea] = useState<MapArea | null>(null)
  const [patrolZones, setPatrolZones] = useState<GeoJSON.FeatureCollection | null>(null)
  const [license, setLicense] = useState<LicenseView | null>(null)
  const [can, setCan] = useState<Set<string>>(new Set())
  // Quién mira. Hace falta para la entrega de turno: quien entregó no puede recibir, y el botón
  // no debe ofrecerle un 409.
  const [actor, setActor] = useState('')
  const [section, setSection] = useState<Area>('command')
  // Qué le toca a cada rol para el turno que se prepara (H-68).
  const [readiness, setReadiness] = useState<Readiness | null>(null)
  const [sets, setSets] = useState<HotspotSet[]>([])
  const [setDetail, setSetDetail] = useState<HotspotSetDetail | null>(null)
  const [setChanges, setSetChanges] = useState<SetChanges | null>(null)
  const [fleetHistory, setFleetHistory] = useState<VehicleHistory | null>(null)
  const [handover, setHandover] = useState<Handover | null>(null)
  const [handoverPreview, setHandoverPreview] = useState<HandoverSnapshot | null>(null)
  const [timeline, setTimeline] = useState<Timeline | null>(null)
  const [logHours, setLogHours] = useState(12)
  hoursRef.current = logHours
  const [sources, setSources] = useState<Source[]>([])
  const [ingestions, setIngestions] = useState<IngestionRun[]>([])
  const [quality, setQuality] = useState<QualityReport | null>(null)
  // Los históricos del explorador de ATLAS. `incidents` ya son los de SENTINEL, que son los
  // que están pasando ahora: dos cosas distintas con el mismo nombre confunden a quien lea.
  const [historical, setHistorical] = useState<IncidentPage | null>(null)
  const [insightShifts, setInsightShifts] = useState<ShiftPerformance[]>([])
  const [insightReview, setInsightReview] = useState<ShiftReview | null>(null)
  const [rebuilt, setRebuilt] = useState<BackfillResult | null>(null)
  const [trends, setTrends] = useState<Trends | null>(null)
  const [profiles, setProfiles] = useState<StrategyProfile[]>([])
  // Arranca en hoy y en el turno de tarde solo hasta que el servidor diga cuál corre de verdad:
  // el turno en curso sale del reloj y de la zona de la agencia, no del reloj del navegador.
  // Hoy en la agencia, no en UTC: por la tarde en Texas, toISOString() ya daba «mañana» (H-01).
  const [shiftDate, setShiftDate] = useState(() => agencyToday())
  const [shiftCode, setShiftCode] = useState('EVENING')
  // El turno que la guía propone se usa una vez, al entrar; después manda lo que la persona elija.
  const turnoElegido = useRef(false)
  const shiftDateRef = useRef(shiftDate)
  shiftDateRef.current = shiftDate
  const shiftCodeRef = useRef(shiftCode)
  shiftCodeRef.current = shiftCode
  const [shiftNow, setShiftNow] = useState<ShiftNow | null>(null)
  // El turno en curso sin plan publicado: el Centro de Mando no tiene contra qué vigilar, y quien
  // debe aprobarlo entra justo por aquí (H-97).
  const enCursoSinPlan = shiftNow?.current != null && (shiftNow.running ?? []).some((r) =>
    r.window.shiftDate === shiftNow.current!.shiftDate && r.window.shiftCode === shiftNow.current!.shiftCode && !r.hasPlan)
  const turnoEnCursoRef = useRef<{ shiftDate: string; shiftCode: string } | null>(null)
  useEffect(() => { turnoEnCursoRef.current = shiftNow?.current ?? null }, [shiftNow])
  const [profileComparison, setProfileComparison] = useState<ProfileComparison | null>(null)
  const [shiftSummary, setShiftSummary] = useState<ShiftSummary | null>(null)
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [roster, setRoster] = useState<ShiftRoster | null>(null)
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [officers, setOfficers] = useState<OfficerProfile[]>([])
  const [availability, setAvailability] = useState<AvailabilityEntry[]>([])
  const [devices, setDevices] = useState<Device[]>([])
  const [audit, setAudit] = useState<AuditEntry[]>([])
  const [auditKind, setAuditKind] = useState('')
  const [targets, setTargets] = useState<StaffingTarget[]>([])
  const [jurisdictions, setJurisdictions] = useState<Jurisdiction[]>([])
  const [shiftTemplates, setShiftTemplates] = useState<ShiftTemplate[]>([])
  const [districts, setDistricts] = useState<District[]>([])
  const [zones, setZones] = useState<District[]>([])
  const [deviation, setDeviation] = useState<DeviationPolicyView | null>(null)
  const [approval, setApproval] = useState<ApprovalPolicyView | null>(null)
  const [formation, setFormation] = useState<FormationPolicyView | null>(null)
  const [deadLetters, setDeadLetters] = useState<DeadLetterSummary | null>(null)
  const [deadLetterItems, setDeadLetterItems] = useState<DeadLetterItem[]>([])
  const [revision, setRevision] = useState<PendingRevision | null>(null)
  /**
   * Una versión nueva que propuso el planificador para el turno en curso (no una reoptimización):
   * el jefe de turno entra por el Centro de Mando y no se enteraba de que lo esperaba (H-97).
   */
  const [propuestaEnCurso, setPropuestaEnCurso] = useState<{ version: number; proposedBy: string } | null>(null)
  const propuestaAprobable = propuestaEnCurso !== null && can.has('plan.approve') && !revision
  const [simulation, setSimulation] = useState<Simulation | null>(null)
  const [provider, setProvider] = useState<OidcSettings | null>(null)
  // Si el servidor no contestó al abrir: se dice así y se vuelve a preguntar solo (H-58).
  const [unreachable, setUnreachable] = useState(false)
  // Hasta que el servidor conteste no se sabe si hay proveedor: decir «no hay» mientras tanto era
  // afirmar algo falso (se veía al abrir la página o al reiniciar la API).
  const [capabilitiesKnown, setCapabilitiesKnown] = useState(false)
  // La agencia de esta dirección, si es el subdominio de una. En la general, nula: se ve PROTECT.
  const [hostAgency, setHostAgency] = useState<{ tenantId: string; agencyId: string } | null>(null)
  const [capabilityTry, setCapabilityTry] = useState(0)
  const [signedInAs, setSignedInAs] = useState<string | null>(null)
  const signIn = useRef<UserManager | null>(null)

  const alertPoints = useMemo(() => alertPointsOf(alerts, assignments, units), [alerts, assignments, units])

  /** Elegir una alerta elige también su unidad: es lo mismo visto desde el otro lado. */
  const chooseAlert = useCallback((alertId: string | null) => {
    setSelectedAlert(alertId)
    setNearbyOnly(false)
    setSamePlaceOnly(false)
    setSelected(alertId === null ? null : (alerts.find((a) => a.alertId === alertId)?.patrolUnitId ?? null))
  }, [alerts])

  /** Las que ocurrieron a menos de NEARBY_METERS de la elegida. */
  const nearbyIds = useMemo(
    () => (selectedAlert ? nearbyAlertIds(alertPoints, selectedAlert, NEARBY_METERS) : null),
    [alertPoints, selectedAlert],
  )

  /** Las que comparten exactamente el punto con la elegida (revisitas al mismo hotspot). */
  const samePlaceIds = useMemo(
    () => (selectedAlert ? new Set(samePlaceAlertIds(alertPoints, selectedAlert)) : null),
    [alertPoints, selectedAlert],
  )

  /** De qué visita del plan habla cada alerta. */
  const visitOf = useCallback(
    (alert: Alert) => {
      const assignment = alert.assignmentId ? assignments.find((a) => a.assignmentId === alert.assignmentId) : undefined
      return assignment ? { sequence: assignment.sequence, plannedArrivalAt: assignment.plannedArrivalAt } : null
    },
    [assignments],
  )

  /** La cola tal como se ve: buscada, acotada al mapa o al grupo cercano, y ordenada. */
  const shownAlerts = useMemo(() => {
    const located = new Map(alertPoints.map((p) => [p.alertId, p]))
    const filtered = alerts.filter((a) => {
      if (!matchesQuery(a, query)) return false
      if (samePlaceOnly && samePlaceIds && !samePlaceIds.has(a.alertId)) return false
      if (nearbyOnly && nearbyIds && !nearbyIds.has(a.alertId)) return false
      if (onlyVisible && bounds) {
        const point = located.get(a.alertId)
        return point ? withinBounds(point, bounds) : false
      }
      return true
    })
    return sortAlerts(filtered, sort)
  }, [alerts, alertPoints, query, sort, onlyVisible, bounds, nearbyOnly, nearbyIds, samePlaceOnly, samePlaceIds])

  /**
   * Qué sabe hacer este despliegue: si hay proveedor de identidad se usa el ingreso real, y el
   * panel de demostración solo aparece donde está permitido.
   *
   * Si la página se abrió como vuelta del proveedor, aquí termina el ingreso: el token que emitió
   * es el que la API valida, y el de demostración deja de usarse.
   */
  useEffect(() => {
    let reintento: number | undefined
    void (async () => {
      const capability = await capabilities()
      setUnreachable(!capability.reachable)
      if (!capability.reachable) {
        // Un servidor que está arrancando contesta en segundos: no hace falta recargar a mano.
        reintento = window.setTimeout(() => setCapabilityTry((n) => n + 1), 5000)
        return
      }
      setDemo(capability.demo)
      setStorage(capability.storage)
      setMfa(capability.mfa ?? null)
      if (capability.map) setMapBase(capability.map)
      // En el subdominio de una agencia, la dirección dice de quién es la pantalla de ingreso.
      const propia = capability.agency ?? null
      setHostAgency(propia)
      if (propia) setConnection((c) => (c.tenantId === propia.tenantId && c.agencyId === propia.agencyId ? c
        : { ...c, tenantId: propia.tenantId, agencyId: propia.agencyId }))
      setLicense(capability.license)
      demoRef.current = capability.demo
      setProvider(capability.oidc)
      setCapabilitiesKnown(true)
      if (!capability.oidc) return

      const manager = createSignIn(capability.oidc)
      signIn.current = manager
      manager.events.addUserLoaded((user) => setToken(user.access_token))
      const user = await completeSignIn(manager)
      if (user && !user.expired) {
        setToken(user.access_token)
        setSignedInAs(user.profile.preferred_username ?? user.profile.name ?? null)
      }
    })()
    return () => window.clearTimeout(reintento)
  }, [capabilityTry])

  // Las áreas de §16.3, cada una con la capacidad que la abre. Se muestran solo las que la
  // persona puede usar: un menú lleno de puertas cerradas no informa, estorba.
  const areas = useMemo(
    () => AREAS.filter((a) => a.capabilities.some((c) => can.has(c))),
    [can])

  // Se entra por la primera área disponible: el analista no aterriza en una pantalla de mando
  // vacía preguntándose qué hace mal.
  useEffect(() => {
    if (areas.length > 0 && !areas.some((a) => a.id === section)) setSection(areas[0]!.id)
  }, [areas, section])

  // Qué puede hacer quien acaba de entrar (§7). Se pregunta al backend en cada sesión: lo que vale
  // es lo que él concede, no lo que el token parezca decir.
  useEffect(() => {
    if (!token) { setCan(new Set()); return }
    let reintento: number | undefined
    void (async () => {
      const quien = await quienSoy(token)
      if (quien === 'unreachable') {
        // El servidor no contesta: la sesión sigue siendo buena. Se vuelve a preguntar solo.
        reintento = window.setTimeout(() => setMeTry((n) => n + 1), 3000)
        return
      }
      if (quien && 'denied' in quien) {
        // La cuenta es de otra agencia que la de esta dirección: de vuelta al ingreso, diciendo cuál
        // es la suya. La sesión del proveedor sigue: allá entra sin volver a escribir la contraseña.
        sessionStorage.removeItem('token')
        setToken(null)
        setCan(new Set())
        setActor('')
        setProblem(quien.denied)
        return
      }
      if (!quien) {
        // El token venció o el servidor lo rechaza. Volver al ingreso dice qué pasó; quedarse
        // con una pantalla sin áreas hace creer que el problema son los permisos.
        sessionStorage.removeItem('token')
        setToken(null)
        setCan(new Set())
        setActor('')
        return
      }
      setCan(new Set(quien.capabilities))
      setAgencyArea(quien.agencyBounds ?? null)
      // La agencia la dice la cuenta del proveedor, no este navegador. Se recuerda para que el
      // ingreso de la próxima vez ya se vea con su marca.
      if (quien.tenant && quien.agency) {
        localStorage.setItem('tenant', quien.tenant)
        localStorage.setItem('agency', quien.agency)
        setConnection((c) => (c.tenantId === quien.tenant && c.agencyId === quien.agency ? c
          : { ...c, tenantId: quien.tenant, agencyId: quien.agency }))
      }
      setActor(quien.subject)
      // Los turnos de la agencia, para todo selector de turno (hallazgo H-05). Si no llegan, quedan
      // los de fábrica, que es lo mismo que usa el servidor cuando la agencia no definió los suyos.
      try { setAgencyShifts(await tactix.shiftTemplates()) } catch { /* los de fábrica */ }
    })()
    return () => window.clearTimeout(reintento)
  }, [token, tactix, meTry])

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? 'dark' : 'light'
    localStorage.setItem('theme', dark ? 'dark' : 'light')
  }, [dark])

  useEffect(() => {
    document.documentElement.lang = lang
    document.title = translate(lang, 'app.title')
    localStorage.setItem('lang', lang)
  }, [lang])

  /**
   * Una llamada al backend: un rechazo se muestra por su código, nunca revienta la pantalla.
   *
   * El `area` sirve para los cuatro estados (§13): mientras un área no haya cargado bien ni una
   * vez, sus listas **no pueden decir «no hay ninguno»**, porque no lo saben. Antes lo decían: la
   * lista vacía inicial y la lista realmente vacía se veían igual.
   */
  // El rechazo de aprobar o devolver una revisión se muestra en el panel y se queda ahí: en la
  // cabecera lo borraba el refresco siguiente (H-39).
  const [revisionProblem, setRevisionProblem] = useState<{ version: number; denial: Denial } | null>(null)
  // Devuelve si salió bien: la pantalla que guarda lo necesita para decir «guardado» junto al botón,
  // y para no tirar lo editado si el servidor lo rechazó (H-74).
  const guard = useCallback(async (action: () => Promise<void>, area?: LoadKey): Promise<boolean> => {
    setBusy(true)
    try {
      await action()
      setProblem(null)
      if (area) {
        setLoaded((previo) => (previo.has(area) ? previo : new Set(previo).add(area)))
        setFailures((previo) => {
          if (!previo.has(area)) return previo
          const siguiente = new Map(previo)
          siguiente.delete(area)
          return siguiente
        })
      }
      return true
    } catch (e) {
      const denial = e instanceof DeniedError ? e.denial : { code: 'app.unreachable', reason: String(e) }
      setProblem(denial)
      if (e instanceof DeniedError && e.denial.code === 'app.session_expired') setToken(null)
      // El fallo se anota **en la carga que falló**. Si se usara el rechazo global, un «no puede
      // aprobar» al firmar un perfil dejaría todas las listas de la aplicación diciendo que
      // muestran datos viejos, que no es cierto y asusta sin motivo.
      if (area) setFailures((previo) => new Map(previo).set(area, denial))
      return false
    } finally {
      setBusy(false)
    }
  }, [])

  // Mientras una ingesta corre, su historial se sigue: la pantalla prometía que «se actualiza al
  // terminar» y solo se leía al entrar al área (H-87). Al terminar, también la calidad de datos.
  const ingestaCorriendo = ingestions.some((r) => r.status === 'RUNNING')
  useEffect(() => {
    if (section !== 'analyst' || !ingestaCorriendo) return
    const id = window.setInterval(() => void (async () => {
      try {
        const nuevas = await atlas.ingestions()
        setIngestions(nuevas)
        if (!nuevas.some((r) => r.status === 'RUNNING')) setQuality(await atlas.quality())
      } catch { /* la próxima vuelta lo intenta de nuevo */ }
    })(), 5000)
    return () => window.clearInterval(id)
  }, [section, ingestaCorriendo, atlas])

  // Los conjuntos de hotspots, al entrar al área. No van en la recarga periódica del turno:
  // un conjunto cambia cuando alguien lo calcula, no cada veinte segundos.
  useEffect(() => {
    if (section !== 'analyst' || !can.has('hotspots.read')) return
    void guard(async () => {
      setSets(await atlas.sets())
      setSources(await atlas.sources())
      setIngestions(await atlas.ingestions())
      // La calidad se mira al entrar, no se pide con un botón: es lo primero que hay que saber
      // antes de creerle a cualquier cálculo hecho con estos datos.
      setQuality(await atlas.quality())
    }, 'analyst')
  }, [section, can, atlas, guard])

  // Qué turno corre, al entrar. Es lo que evita que el supervisor tenga que escribir la fecha de
  // su propio turno, que es de las cosas que más rápido delatan que un sistema no entiende el
  // trabajo que acompaña.
  //
  // Se vuelve a preguntar cada minuto: se leía una sola vez al entrar, y a las 19:00 UTC la
  // cabecera seguía diciendo «INTEGRAL, quedan 2 h 34 min» con otro turno ya empezado (H-34). La
  // selección de quien está trabajando solo se mueve si cambia el turno que se entregaría.
  useEffect(() => {
    if (!token || !can.has('shift.monitor')) return
    let previo: string | null = null
    const leer = () => void guard(async () => {
      const ahora = await api.shiftNow()
      setShiftNow(ahora)
      const suyo = ahora.toHandOver
      const clave = suyo ? `${suyo.shiftDate}/${suyo.shiftCode}` : null
      if (suyo && clave !== previo) { setShiftDate(suyo.shiftDate); setShiftCode(suyo.shiftCode) }
      previo = clave
    })
    leer()
    const reloj = window.setInterval(leer, 60_000)
    return () => window.clearInterval(reloj)
  }, [token, can, api, guard])

  // La bitácora sí se vuelve a leer al cambiar la ventana, y con cada recarga del turno: es lo
  // único de esta pantalla cuyo valor está en estar al día.
  useEffect(() => {
    if (section !== 'command' || !can.has('shift.monitor')) return
    void guard(async () => setTimeline(await api.timeline(logHours)), 'timeline')
  }, [section, can, api, guard, logHours])

  // La entrega del turno elegido. No va en la recarga periódica: una entrega cambia cuando
  // alguien firma, no cada veinte segundos, y la foto que ya se firmó no cambia nunca.
  useEffect(() => {
    if (section !== 'command' || !can.has('shift.monitor')) return
    // Si se cambia de turno antes de que llegue la respuesta, esta ya no es la que se mira (H-13).
    let vigente = true
    void guard(async () => {
      const firmada = await api.handover(shiftDate, shiftCode)
      // La vista previa solo hace falta mientras no se haya entregado: después, lo que se
      // muestra es la foto, no la situación de ahora.
      const previa = firmada === null ? await api.handoverPreview(shiftDate, shiftCode) : null
      if (!vigente) return
      setHandover(firmada)
      setHandoverPreview(previa)
    }, 'handover')
    return () => { vigente = false }
  }, [section, can, api, guard, shiftDate, shiftCode])

  // La vista previa, en cambio, es la situación de ahora: se vuelve a pedir al abrir la pestaña
  // Turno y cuando cambian los incidentes o el estado de las unidades. Antes se quedaba con la foto
  // de cuando se entró: listaba como «Activo» un incidente terminado hacía minutos.
  const situacionTurno = incidents.map((i) => `${i.incidentId}:${i.status}`).join(',') + '|' +
    units.map((u) => `${u.callSign}:${u.status}`).join(',')
  useEffect(() => {
    if (section !== 'command' || commandTab !== 'shift' || !can.has('shift.monitor') || handover !== null) return
    let vigente = true
    void guard(async () => {
      const previa = await api.handoverPreview(shiftDate, shiftCode)
      if (vigente) setHandoverPreview(previa)
    }, 'handover')
    return () => { vigente = false }
  }, [section, commandTab, can, api, guard, shiftDate, shiftCode, handover, situacionTurno])

  // Los perfiles, al entrar al área. No cambian solos: cambian cuando alguien firma.
  useEffect(() => {
    if (section !== 'strategy' || !can.has('strategy.read')) return
    void guard(async () => setProfiles(await strategy.profiles()), 'strategy')
  }, [section, can, strategy, guard])

  // Los informes, al entrar al área. Son del turno pasado: no cambian mientras se miran.
  useEffect(() => {
    if (section !== 'insight' || !can.has('reports.read')) return
    void guard(async () => {
      setInsightShifts(await insight.shifts())
      setTrends(await insight.trends())
      setInsightReview(null)
    }, 'insight')
  }, [section, can, insight, guard])

  // El turno del planner: al entrar al área y cada vez que cambia la fecha o el turno. No va en
  // la recarga periódica, que es del turno en curso y no del que se está planeando.
  // Cuál es el último turno pedido. Al cambiar de turno, la respuesta del anterior puede llegar
  // después que la del nuevo, y pisarla: la pantalla decía INTEGRAL y mostraba los números de DAY
  // (hallazgo H-13 de la prueba integral). Solo se aplica la respuesta del último pedido.
  const ultimoTurno = useRef(0)
  const loadShift = useCallback(async () => {
    const pedido = ++ultimoTurno.current
    await guard(async () => {
      const [resumen, lista, plantilla] = await Promise.all([
        tactix.shift(shiftDate, shiftCode),
        tactix.scenarios(shiftDate, shiftCode),
        tactix.roster(shiftDate, shiftCode),
      ])
      if (pedido !== ultimoTurno.current) return
      setShiftSummary(resumen)
      setScenarios(lista)
      setRoster(plantilla)
    }, 'planner')
  }, [guard, tactix, shiftDate, shiftCode])

  useEffect(() => {
    if (section !== 'planner' || !token) return
    void loadShift()
  }, [section, token, loadShift])

  // Flota y personal: el catálogo y lo publicado para el turno que se está mirando.
  // Como con el plan (H-13): si llegara tarde la disponibilidad de otro turno, se publicaría la
  // lista equivocada, y publicar reemplaza la lista completa del turno.
  const ultimosRecursos = useRef(0)
  const loadResources = useCallback(async (kind: 'fleet' | 'hr') => {
    const pedido = ++ultimosRecursos.current
    await guard(async () => {
      const publicada = await tactix.availability(shiftDate, shiftCode, kind === 'fleet' ? 'vehicle' : 'officer')
      const catalogo = kind === 'fleet' ? await tactix.vehicles() : null
      const plantilla = kind === 'hr' ? await tactix.officers() : null
      if (pedido !== ultimosRecursos.current) return
      setAvailability(publicada)
      if (catalogo) setVehicles(catalogo)
      if (plantilla) setOfficers(plantilla)
    }, kind)
  }, [guard, tactix, shiftDate, shiftCode])

  useEffect(() => {
    // Lo mismo al cambiar de usuario: sin el permiso de la sección, no se pide nada.
    if (!token || (section !== 'fleet' && section !== 'hr')) return
    if (!can.has(section === 'fleet' ? 'fleet.manage' : 'officers.manage')) return
    void loadResources(section)
  }, [section, token, loadResources, can])

  // Las zonas de patrullaje del mapa (§10.1). Una vez al abrir el Centro de Mando: cambian cuando
  // alguien sube un archivo, no minuto a minuto. Sin zonas —o si no se pueden leer— el mapa sigue
  // sin ellas: son contexto, no lo que el operador vigila.
  useEffect(() => {
    // Solo con el permiso de vigilar el turno: al cambiar de usuario, la sección del anterior sigue
    // puesta un instante y la petición salía con un token que no puede leerlas (403).
    if (!token || section !== 'command' || !can.has('shift.monitor')) return
    void api.patrolZones().then(setPatrolZones, () => setPatrolZones(null))
  }, [token, section, api, can])

  useEffect(() => {
    if (!token || !(PREPARAN.has(section) || (section === 'command' && enCursoSinPlan))) return
    // La primera vez, el turno lo propone el servidor y las pantallas se abren en él; después, la
    // guía sigue el turno que la pantalla tiene elegido (H-76).
    const traer = () => void (async () => {
      try {
        if (!turnoElegido.current) {
          const propuesta = await tactix.readiness()
          if (propuesta) {
            turnoElegido.current = true
            setShiftDate(propuesta.shiftDate)
            setShiftCode(propuesta.shiftCode)
          }
          setReadiness(propuesta)
          return
        }
        setReadiness(await tactix.readiness({ date: shiftDateRef.current, code: shiftCodeRef.current }))
      } catch {
        setReadiness(null)
      }
    })()
    traer()
    refrescarGuia.current = traer
    const cada = window.setInterval(traer, 60_000)
    return () => { window.clearInterval(cada); refrescarGuia.current = null }
  }, [token, section, tactix, shiftDate, shiftCode, enCursoSinPlan])

  // Al terminar cualquier acción la guía se vuelve a leer: después de proponer seguía diciendo
  // «le toca al planificador» hasta un minuto (H-96).
  useEffect(() => {
    if (!busy) refrescarGuia.current?.()
  }, [busy])

  // Administración: los teléfonos y la bitácora, cada uno con su capacidad.
  const loadAdmin = useCallback(async () => {
    await guard(async () => {
      if (can.has('devices.manage')) setDevices(await adminApi.devices())
      if (can.has('audit.read')) {
        setAudit(await adminApi.audit(auditKind))
        setDeadLetters(await adminApi.deadLetters())
        setDeadLetterItems(await adminApi.deadLetterItems())
      }
      if (can.has('agency.read')) {
        setTargets(await adminApi.staffingTargets())
        setJurisdictions(await adminApi.jurisdictions())
        setDeviation(await adminApi.deviationPolicy())
        setApproval(await adminApi.approvalPolicy())
        setFormation(await adminApi.formationPolicy())
        // Los turnos, los límites y las zonas también: sin esto la pantalla se abre diciendo
        // «sin límites cargados» a una agencia que los tiene, y solo aparecen si alguien guarda.
        setShiftTemplates(await adminApi.shiftTemplates())
        setDistricts(await adminApi.districts())
        setZones(await adminApi.patrolZones())
      }
    }, 'admin')
  }, [guard, adminApi, can, auditKind])

  useEffect(() => {
    if (!token || section !== 'admin') return
    void loadAdmin()
  }, [section, token, loadAdmin])

  // Las capas del analista (H-65): distritos y zonas, que antes solo se veían en Administración.
  useEffect(() => {
    if (!token || section !== 'analyst' || !can.has('agency.layers')) return
    void adminApi.districts().then(setDistricts, () => undefined)
    void adminApi.patrolZones().then(setZones, () => undefined)
  }, [section, token, can, adminApi])

  const load = useCallback(async () => {
    // Solo quien vigila el turno: para el analista, «Actualizar» pedía las unidades del Centro de
    // Mando, el servidor lo negaba (403) y la barra decía «Sin conexión con el servidor» (demo, 27-sep).
    if (!tokenRef.current || !puedeVigilarRef.current) return
    await guard(async () => {
      const [u, a, al, inc, log] = await Promise.all([
        api.units(), api.assignments(), api.alerts(showAllAlerts), api.incidents(),
        // La bitácora va con lo demás: su valor está en estar al día, que es lo que la separa
        // de la bitácora del auditor.
        api.timeline(hoursRef.current),
      ])
      setTimeline(log)
      // La bitácora también se carga sola al abrirla, con su propia marca de error. Si esa carga
      // falló y esta trae la bitácora bien, el panel no puede seguir diciendo «no se pudo cargar»
      // sobre datos al día (prueba integral, Acto 4).
      setFailures((previo) => {
        if (!previo.has('timeline')) return previo
        const siguiente = new Map(previo)
        siguiente.delete('timeline')
        return siguiente
      })
      setLoaded((previo) => (previo.has('timeline') ? previo : new Set(previo).add('timeline')))
      setUnits(u)
      setAssignments(a)
      setAlerts(al)
      setIncidents(inc)
    }, 'command')
    // La reoptimización que espera aprobación en el turno que corre, con su delta (§15.1; MA-04).
    const enCurso = turnoEnCursoRef.current
    // Las dos juntas: por separado, al aprobar una revisión quedaba un momento sin revisión y con
    // la propuesta vieja, y aparecía el aviso de «propuso la versión N» ya aprobada.
    const [rev, resumen] = enCurso
      ? await Promise.all([
        tactix.revision(enCurso.shiftDate, enCurso.shiftCode).catch(() => null),
        tactix.shift(enCurso.shiftDate, enCurso.shiftCode).catch(() => null),
      ])
      : [null, null]
    setRevision(rev)
    setPropuestaEnCurso(resumen?.proposed && !rev ? { version: resumen.proposed.version, proposedBy: resumen.proposed.proposedBy } : null)
    if (demoRef.current) setSimulation(await api.simulation().catch(() => null))
  }, [api, tactix, guard, showAllAlerts])

  /** Aprobar o devolver la revisión: si se rechaza, el motivo queda en el panel (H-39). */
  const decideRevision = useCallback((version: number, action: () => Promise<void>) => guard(async () => {
    try {
      await action()
      setRevisionProblem(null)
    } catch (e) {
      if (e instanceof DeniedError) setRevisionProblem({ version, denial: e.denial })
      throw e
    }
    await load()
  }), [guard, load])

  // Tiempo real + consulta periódica: el canal avisa, la API manda (ADR-0002).
  //
  // Solo para quien vigila el turno. El hub exige esa capacidad, así que abrirlo con un token de
  // flota o de personal falla, y la barra acababa diciéndole «sin conexión con el servidor» a
  // alguien que no necesita ninguna conexión en vivo.
  const watchesShift = can.has('shift.monitor')
  puedeVigilarRef.current = watchesShift
  useEffect(() => {
    if (!token || !watchesShift) return
    let hub: HubConnection | null = null
    hub = connectRealtime(
      () => tokenRef.current,
      {
        onUnitChanged: (unit) => setUnits((current) => mergeUnit(current, unit as Partial<UnitView>)),
        onAlertChanged: (alert) => setAlerts((current) => mergeAlert(current, alert as Alert)),
        onReoptimizationReady: (outcome) => {
          // Lo que manda el servidor es el evento ReoptimizationCompleted. Se leían campos que no
          // trae (`changed`, `delta.added`): decía «Proposed: 0 asignaciones cambiadas» con quince
          // cambios a la vista (H-35).
          const o = outcome as {
            outcome?: string; shiftCode?: string; proposedVersion?: number | null; added?: number; removed?: number
          }
          const clave = o.outcome === 'Proposed' || o.outcome === 'NoChanges' || o.outcome === 'Failed' ? o.outcome : 'Failed'
          setReplanResult(translate(lang, `replan.result.${clave}`, [
            o.shiftCode ?? '', String(o.proposedVersion ?? ''), String(o.added ?? 0), String(o.removed ?? 0),
          ]))
          void load()
        },
        onState: setLink,
      },
      () => void load(),
    )
    const timer = window.setInterval(() => void load(), REFRESH_MS)
    return () => {
      window.clearInterval(timer)
      stopRealtime(hub)
    }
  }, [token, watchesShift, load, lang])

  useEffect(() => { if (watchesShift) void load() }, [watchesShift, load])

  // La marca de la agencia de quien ingresó (§16.3d): la barra de arriba la muestra a todos, y
  // Administración la cambia. Sale del token, no de lo que recuerde el navegador.
  const [marca, setMarca] = useState<Branding | null>(null)
  useEffect(() => { setAgencyZone(marca?.timeZoneId) }, [marca])
  // Las distancias en las unidades de la agencia (H-64). Se fija al dibujar, antes que los hijos:
  // la marca cambia rara vez, y así ninguna pantalla muestra metros un instante.
  setUnitSystem(marca?.unitSystem)
  useEffect(() => {
    if (!token) { setMarca(null); return }
    let vigente = true
    void myBranding(token).then((b) => { if (vigente) setMarca(b) })
    return () => { vigente = false }
    // También cuando el servidor por fin contesta quién es (actor): si la página se abrió con la
    // API caída, la marca se pidió y no llegó, y la barra quedaba con el identificador de la agencia.
  }, [token, actor])

  // Los colores de la agencia en toda la aplicación (§16.3d), para el tema que esté puesto. Sin
  // agencia todavía, los de PROTECT: el azul marino y el cian del diseño. Los de estado no cambian.
  const paleta = useMemo(() => appPalette(marca?.primaryColor ?? PRODUCT_BRANDING.primaryColor,
    marca?.accentColor ?? PRODUCT_BRANDING.accentColor, dark), [marca, dark])
  useEffect(() => {
    const raiz = document.documentElement.style
    for (const [token, valor] of Object.entries(paleta)) raiz.setProperty(token, valor)
    return () => { for (const token of Object.keys(paleta)) raiz.removeProperty(token) }
  }, [paleta])

  /** Cambia el tema y trae el nuevo; dice si se pudo, para que el editor no anuncie lo que no pasó. */
  const changeBranding = async (change: () => Promise<unknown>): Promise<boolean> => {
    let ok = false
    await guard(async () => {
      await change()
      setMarca(await myBranding(tokenRef.current ?? ''))
      ok = true
    })
    return ok
  }

  const signOut = () => {
    sessionStorage.removeItem('token')
    setToken(null)
    setSignedInAs(null)
    // Cerrar aquí y seguir con la sesión abierta en el proveedor sería cerrar solo a medias.
    if (signIn.current) void signIn.current.signoutRedirect().catch(() => signIn.current?.removeUser())
    setUnits([])
    setAlerts([])
    setIncidents([])
    setAssignments([])
  }

  if (!token) {
    return (
      <SignIn
        lang={lang}
        agency={hostAgency}
        busy={busy}
        problem={problem}
        provider={provider}
        unreachable={unreachable}
        known={capabilitiesKnown}
        storage={storage}
        mapBase={mapBase}
        onProviderSignIn={() => void signIn.current?.signinRedirect()}
        onToggleTheme={() => setDark(!dark)}
        onToggleLang={() => setLang(lang === 'en' ? 'es' : 'en')}
      />
    )
  }

  return (
    <div className="app">
      {/* El encabezado y el aviso de demostración son un solo renglón de la rejilla: suelto, el aviso
          tomaba el renglón que estira y se comía media pantalla. */}
      <div className="app-top">
      <header className="topbar">
        {/*
          La marca en dos renglones: el nombre de la agencia debajo del título, no a su lado. De
          lado gastaba ancho dos veces y era lo primero que empujaba a los botones fuera de la
          pantalla. Debajo no cuesta nada, porque el título no llena el alto de la barra.
        */}
        <div className="brand-row" style={marca ? { '--brand-bg': marca.primaryColor, '--brand-accent': marca.accentColor } as React.CSSProperties : undefined}>
          {marca?.agencyName && <AgencyBadge name={marca.agencyName} src={badgeOf(marca)} className="topbar-badge" />}
          <div className="brand">
            <h1>{translate(lang, 'app.title')}</h1>
            <span className="notice agency" title={marca?.agencyId || connection.agencyId}>
              {marca?.agencyName || connection.agencyId}
              {signedInAs && ` · ${signedInAs}`}
            </span>
          </div>
        </div>

        {watchesShift && (
          // Etiqueta corta en la pastilla y la explicación al pasar el cursor: una frase entera
          // aquí se parte en tres renglones y deforma la barra.
          <span
            className={`chip ${link === 'live' ? 'ok' : link === 'connecting' ? 'warning' : 'danger'}`}
            title={translate(lang, `link.${link}.help`)}
          >
            {translate(lang, `link.${link}`)}
          </span>
        )}

        {areas.length > 1 && (
          <nav className="areas">
            {areas.map((a) => (
              <button
                key={a.id}
                className={section === a.id ? 'primary' : ''}
                onClick={() => setSection(a.id)}
              >
                {translate(lang, `nav.${a.id}`)}
              </button>
            ))}
          </nav>
        )}

        <div className="spacer" />
        {problem && <span className="error">{denialText(lang, problem)}</span>}

        {/* Las acciones van juntas: si la barra se parte, se bajan todas y no una suelta. */}
        <div className="actions">
          <button onClick={() => { void load(); refrescarGuia.current?.() }} disabled={busy}>{translate(lang, 'app.refresh')}</button>
          <button onClick={() => setLang(lang === 'en' ? 'es' : 'en')} title={translate(lang, 'app.language')}>
            {lang === 'en' ? 'ES' : 'EN'}
          </button>
          {/* Solo si quien mira está en otra hora que la agencia: si no, no hay nada que elegir (H-01). */}
          {zonesDiffer() && (
            <button
              onClick={() => setTimeMode(timeMode() === 'agency' ? 'mine' : 'agency')}
              title={translate(lang, timeMode() === 'agency' ? 'time.toMine' : 'time.toAgency')}
            >
              {translate(lang, timeMode() === 'agency' ? 'time.agency' : 'time.mine', [zoneLabel()])}
            </button>
          )}
          <button onClick={() => setDark(!dark)} title={translate(lang, 'app.theme')}>{dark ? '☀' : '☾'}</button>
          <button onClick={signOut}>{translate(lang, 'app.signOut')}</button>
        </div>
      </header>
      <DemoBanner notice={marca?.demoNotice} />
      </div>

      {/* Un rol sin pantalla no aterriza en una de mando vacía preguntándose qué hace mal. */}
      {areas.length === 0 && (
        <div className="layout single">
          <aside className="side wide">
            <p className="notice">{translate(lang, 'app.noAreas')}</p>
          </aside>
        </div>
      )}

      {/* Arriba de cada área que prepara el turno: qué está hecho, qué falta y a quién le toca. */}
      {(PREPARAN.has(section) || (section === 'command' && enCursoSinPlan)) && areas.length > 0 && (
        <ReadinessPanel lang={lang} readiness={readiness} can={can}
          onGo={(area) => {
            if (!areas.some((a) => a.id === area)) return
            // Se abre en el turno que se prepara, no en el que la pantalla tuviera elegido.
            if (readiness) { setShiftDate(readiness.shiftDate); setShiftCode(readiness.shiftCode) }
            setSection(area as Area)
          }} />
      )}

      {areas.length > 0 && section === 'command' && <div className="layout">
        <LiveMap
          background={paleta['--bg']}
          units={units}
          assignments={assignments}
          alertPoints={alertPoints}
          alerts={alerts}
          dark={dark}
          lang={lang}
          selected={selected}
          selectedAlert={selectedAlert}
          onSelect={(unit) => { setSelected(unit); setSelectedAlert(null); if (unit) setCommandTab('units') }}
          onSelectAlert={(alert) => { chooseAlert(alert); if (alert) setCommandTab('now') }}
          incidents={incidents}
          selectedIncident={selectedIncident}
          onSelectIncident={(incident) => { setSelectedIncident(incident); if (incident) setCommandTab('incidents') }}
          onBounds={setBounds}
          base={mapBase}
          area={agencyArea}
          zones={patrolZones}
        />
        <aside className="side">
          <LicenseBanner lang={lang} license={license} />
          <p className={enCursoSinPlan ? 'notice warning' : 'notice'}>
            {enCursoSinPlan
              ? translate(lang, 'app.helpNoPlan', [shiftNow.current!.shiftCode])
              : translate(lang, 'app.help')}
          </p>
          {/* Qué turno corre y cuánto le queda. Lo primero que mira quien llega, y lo que hasta
              ahora había que deducir de la fecha en tres selectores. */}
          {shiftNow?.current && (
            <p className="notice">
              <strong>
                {translate(lang, 'shift.running', [
                  shiftNow.current.shiftDate, shiftNow.current.shiftCode,
                ])}
              </strong>
              {' · '}
              {translate(lang, 'shift.remaining', [
                Math.floor(shiftNow.current.remainingMinutes / 60),
                shiftNow.current.remainingMinutes % 60,
              ])}
              {/* Turnos solapados: que se vea que hay otro, y si tiene gente (H-34). */}
              {(shiftNow.running ?? [])
                .filter((r) => r.window.shiftCode !== shiftNow.current!.shiftCode || r.window.shiftDate !== shiftNow.current!.shiftDate)
                .map((r) => (
                  <span key={`${r.window.shiftDate}/${r.window.shiftCode}`}>
                    <br />
                    {translate(lang, r.hasPlan ? 'shift.alsoRunning' : 'shift.alsoRunningNoPlan', [r.window.shiftCode])}
                  </span>
                ))}
            </p>
          )}
          {shiftNow !== null && shiftNow.current === null && (
            <p className="notice warning">{translate(lang, 'shift.none')}</p>
          )}
          {/* Por etapa del trabajo, como las demás áreas: siete paneles apilados eran un recorrido
              larguísimo. Lo urgente no se esconde: cada pestaña dice cuánto espera adentro, en rojo
              si es crítico, y elegir algo en el mapa lleva a su pestaña. */}
          <Tabs
            area="command"
            active={commandTab}
            onChange={setCommandTab}
            tabs={[
              {
                id: 'now',
                label: translate(lang, 'command.tabNow'),
                badge: {
                  // Para el jefe de turno, lo escalado también espera: es para él (H-66).
                  count: alerts.filter((a) => a.status === 'New' || (a.status === 'Escalated' && can.has('plan.adjust'))).length,
                  critical: alerts.some((a) => (a.status === 'New' && (a.severity === 'Critical' || a.kind === 'SupportRequested'))
                    || (a.status === 'Escalated' && can.has('plan.adjust'))),
                  label: translate(lang, 'command.tabNowBadge'),
                },
                content: (
                  <>
                  <AlertsPanel
                    loaded={loaded.has('command')} problem={failures.get('command') ?? null}
                    onReplan={can.has('reoptimization.request') ? () => setCommandTab('plan') : undefined}
                    canAct={can.has('alerts.act')}
                    lang={lang}
                    alerts={shownAlerts}
                    total={alerts.length}
                    showAll={showAllAlerts}
                    onShowAll={setShowAllAlerts}
                    busy={busy}
                    selected={selectedAlert}
                    onSelect={chooseAlert}
                    located={new Set(alertPoints.map((p) => p.alertId))}
                    query={query}
                    onQuery={setQuery}
                    sort={sort}
                    onSort={setSort}
                    onlyVisible={onlyVisible}
                    onOnlyVisible={setOnlyVisible}
                    nearby={{ count: nearbyIds?.size ?? 0, meters: NEARBY_METERS, only: nearbyOnly }}
                    onNearby={setNearbyOnly}
                    visitOf={visitOf}
                    samePlace={{ count: samePlaceIds?.size ?? 0, only: samePlaceOnly }}
                    onSamePlace={setSamePlaceOnly}
                    onAction={(alert: Alert, action: AlertAction, assignedTo: string, note: string) =>
                      void guard(async () => {
                        const updated = await api.applyAlert(alert.alertId, action, note.trim(), assignedTo)
                        setAlerts((current) => mergeAlert(current, updated))
                      })
                    }
                  />
                  </>
                ),
              },
              {
                // Los incidentes aparte de los desvíos: son pocos y urgentes (un oficial fuera del plan
                // atendiendo algo), y debajo de la lista de desvíos había que bajar toda para encontrarlos.
                id: 'incidents',
                label: translate(lang, 'command.tabIncidents'),
                badge: {
                  count: incidents.filter((i) => i.status === 'Active' || i.status === 'Reported').length,
                  critical: true,
                  label: translate(lang, 'command.tabIncidentsBadge'),
                },
                content: (
                  <IncidentsPanel
                    selected={selectedIncident}
                    onSelect={(i) => { setSelectedIncident(i.incidentId); setSelected(null); setSelectedAlert(null) }}
                    loaded={loaded.has('command')} problem={failures.get('command') ?? null}
                    canAct={can.has('incidents.act')}
                    lang={lang}
                    incidents={incidents}
                    busy={busy}
                    onAction={(incident: Incident, action: IncidentAction, note: string) =>
                      void guard(async () => {
                        const updated = await api.applyIncident(incident.incidentId, action, note)
                        setIncidents((current) => current.map((i) => (i.incidentId === updated.incidentId ? updated : i)))
                      })
                    }
                    onReplan={can.has('reoptimization.request') && shiftNow?.current ? (incident, returning) => void guard(async () => {
                      // El motivo lo dice el incidente: quien aprueba la revisión sabe por qué se pidió.
                      setReplanResult(translate(lang, 'replan.requested'))
                      await api.reoptimize(shiftNow.current!.shiftDate, shiftNow.current!.shiftCode,
                        translate(lang, returning ? 'incidents.reasonReturn' : 'incidents.reasonOut', [incident.callSign, incident.description]))
                    }) : undefined}
                  />
                ),
              },
              {
                id: 'units',
                label: translate(lang, 'command.tabUnits'),
                badge: {
                  count: units.filter((u) => u.connectivity === 'Offline' && u.status !== 'OffDuty' && u.status !== 'EndShift').length,
                  critical: true,
                  label: translate(lang, 'command.tabUnitsBadge'),
                },
                content: (
                  <>
                  <UnitsPanel
                    lang={lang} units={units} assignments={assignments} selected={selected} onSelect={setSelected}
                    loaded={loaded.has('command')} problem={failures.get('command') ?? null}
                    busy={busy}
                    onRadio={can.has('incidents.act') ? async (unit, report, text) => {
                      let ok = false
                      await guard(async () => {
                        if (report === 'incident') await api.registerIncident(unit.patrolUnitId, text)
                        else await api.setUnitAvailability(unit.patrolUnitId, report === 'restored', text)
                        // Lo siguiente es repartir o reincorporar: la pantalla lo dice (§15.1, «llevar de la mano»).
                        setReplanResult(translate(lang, `radio.${report}Done`, [unit.callSign]))
                        // Lo siguiente es reoptimizar: la pantalla lleva a donde se pide.
                        if (can.has('reoptimization.request')) setCommandTab('plan')
                        ok = true
                        await load()
                      })
                      return ok
                    } : undefined}
                    onCancelVisit={can.has('plan.adjust') ? async (assignment, justification) => {
                      let ok = false
                      await guard(async () => {
                        await tactix.cancelAssignment(assignment.assignmentId, justification)
                        ok = true
                        await load()
                      })
                      return ok
                    } : undefined}
                    onReassign={can.has('plan.adjust') ? async (assignment, toUnit, justification) => {
                      let ok = false
                      await guard(async () => {
                        const done = await tactix.reassign(assignment.assignmentId, toUnit, justification)
                        setReplanResult(translate(lang, 'reassign.done', [assignment.label, done.toCallSign, String(done.version)]))
                        ok = true
                        await load()
                      })
                      return ok
                    } : undefined}
                  />
                  </>
                ),
              },
              ...(revision || propuestaAprobable || can.has('reoptimization.request') ? [{
                id: 'plan',
                label: translate(lang, 'command.tabPlan'),
                badge: { count: revision || propuestaAprobable ? 1 : 0, label: translate(lang, 'command.tabPlanBadge') },
                content: (
                  <>
                  {propuestaAprobable && (
                    <div className="card">
                      <p className="notice warning">
                        {translate(lang, 'command.proposalWaiting', [propuestaEnCurso.version, propuestaEnCurso.proposedBy])}
                      </p>
                      <button className="primary" onClick={() => {
                        try { localStorage.setItem('tab:planner', 'plan') } catch { /* abre la primera */ }
                        // El turno en curso, y que la guía no lo cambie al entrar: abría el que ella
                        // proponía preparar (EVENING) y se aprobaba sobre el turno equivocado.
                        if (shiftNow?.current) {
                          turnoElegido.current = true
                          setShiftDate(shiftNow.current.shiftDate); setShiftCode(shiftNow.current.shiftCode)
                        }
                        setSection('planner')
                      }}>
                        {translate(lang, 'command.reviewProposal')}
                      </button>
                    </div>
                  )}
                  {/* Pedir una reoptimización es autoridad del operador y del Watch Commander (§7): quien
                      solo vigila no ve el panel, en vez de verlo y toparse con un 403. */}
                  {revision && (
                    <RevisionPanel
                      key={`${revision.planId}/${revision.version}`}
                      lang={lang} revision={revision} busy={busy} canDecide={can.has('plan.approve')}
                      problem={revisionProblem && revisionProblem.version === revision.version
                        ? denialText(lang, revisionProblem.denial) : null}
                      onApprove={(ack) => void decideRevision(revision.version, async () => {
                        await tactix.approve(revision.planId, revision.version, ack)
                      })}
                      onReject={(reason) => void decideRevision(revision.version, async () => {
                        await tactix.reject(revision.planId, revision.version, reason)
                      })}
                      onRequestAgain={can.has('reoptimization.request') && shiftNow?.current ? () => void guard(async () => {
                        setReplanResult(translate(lang, 'replan.requested'))
                        await api.reoptimize(shiftNow.current!.shiftDate, shiftNow.current!.shiftCode, revision.reason)
                      }) : undefined}
                    />
                  )}
                  {can.has('reoptimization.request') && <ReplanPanel
                    currentShift={shiftNow?.current ?? undefined}
                    lang={lang}
                    busy={busy}
                    result={replanResult}
                    onRequest={(date, code, reason) =>
                      void guard(async () => {
                        // «Pedida» va antes de enviar: una corrida sin cambios tarda decenas de
                        // milisegundos, y su aviso llegaba antes que la respuesta del pedido, que lo
                        // pisaba con «Pedida. El resultado llega a esta pantalla» para siempre (H-36).
                        setReplanResult(translate(lang, 'replan.requested'))
                        try {
                          await api.reoptimize(date, code, reason)
                        } catch (e) {
                          setReplanResult(null)
                          throw e
                        }
                      })
                    }
                  />}
                  </>
                ),
              }] : []),
              {
                id: 'shift',
                label: translate(lang, 'command.tabShift'),
                content: (
                  <>
                  {/* La entrega la lee cualquiera que vigile el turno —el jefe la lee al día siguiente—;
                      firmarla es declarar que uno se hace cargo, y eso pide su propia capacidad. */}
                  <HandoverPanel
                    lang={lang}
                    shiftDate={shiftDate}
                    shiftCode={shiftCode}
                    onShift={(d, c) => { setShiftDate(d); setShiftCode(c) }}
                    preview={handoverPreview}
                    handover={handover}
                    busy={busy}
                    canSign={can.has('shift.handover')}
                    actor={actor}
                    onDeliver={(notes) => void guard(async () => {
                      setHandover(await api.deliverHandover(shiftDate, shiftCode, notes))
                    })}
                    onReceive={(handoverId, note) => void guard(async () => {
                      setHandover(await api.receiveHandover(handoverId, note))
                    })}
                  />
                  <TimelinePanel
                    lang={lang} timeline={timeline} hours={logHours} onHours={setLogHours} busy={busy}
                    loaded={loaded.has('timeline')} problem={failures.get('timeline') ?? null}
                  />
                  {demo && (
                    <DemoPanel
                      lang={lang}
                      simulation={simulation}
                      busy={busy}
                      onStart={(request: SimulationRequest) => void guard(async () => {
                        setSimulation(await api.startSimulation(request))
                        // Lo que hagan las unidades simuladas se ve como cualquier otra cosa: por la API.
                        window.setTimeout(() => void load(), 2000)
                      })}
                      onStop={() => void guard(async () => setSimulation(await api.stopSimulation()))}
                      formResult={formResult}
                      onFormShift={() => void guard(async () => {
                        const r = await api.formShift(shiftDate, shiftCode)
                        setFormResult(r.skipped.length === 0
                          ? translate(lang, 'demo.formed', [r.formed])
                          : translate(lang, 'demo.formedWithSkips', [r.formed, r.skipped.length, r.skipped[0] ?? '']))
                        await load()
                      })}
                    />
                  )}
                  </>
                ),
              },
            ]}
          />
        </aside>
      </div>}

      {section === 'planner' && (
        <PlannerScreen
          loaded={loaded.has('planner')} problem={failures.get('planner') ?? null}
          lang={lang}
          date={shiftDate}
          code={shiftCode}
          onShift={(d, c) => { setShiftDate(d); setShiftCode(c) }}
          summary={shiftSummary}
          scenarios={scenarios}
          busy={busy}
          roster={roster}
          canForm={can.has('units.form')}
          canExcept={can.has('units.except')}
          canGenerate={can.has('plan.create')}
          canPropose={can.has('plan.propose')}
          canApprove={can.has('plan.approve')}
          onEvaluate={async (r: FormUnit): Promise<Evaluation | null> => {
            // La evaluación es continua mientras se arma: un fallo aquí no debe teñir la pantalla
            // de rojo ni bloquear al usuario, así que no pasa por `guard`.
            try {
              return await tactix.evaluate(shiftDate, shiftCode, r)
            } catch {
              return null
            }
          }}
          onFormUnit={(r: FormUnit) => void guard(async () => {
            await tactix.formUnit(shiftDate, shiftCode, r)
            await loadShift()
          })}
          onGenerate={(r: GenerateScenario) => void guard(async () => {
            await tactix.generate(shiftDate, shiftCode, r)
            await loadShift()
          })}
          onPropose={(scenarioId) => void guard(async () => {
            await tactix.propose(scenarioId, '')
            await loadShift()
          })}
          onApprove={(planId, version, partial) => void guard(async () => {
            await tactix.approve(planId, version, partial)
            await loadShift()
          })}
          onReject={(planId, version, reason) => void guard(async () => {
            await tactix.reject(planId, version, reason)
            await loadShift()
          })}
        />
      )}

      {section === 'strategy' && (
        <StrategyScreen
          loaded={loaded.has('strategy')} problem={failures.get('strategy') ?? null}
          lang={lang}
          comparison={profileComparison}
          onCompare={(a, b) => void guard(async () => {
            // Cerrar la comparación es pedirla vacía: el panel no guarda estado propio.
            setProfileComparison(a === '' || b === '' ? null : await strategy.comparison(a, b))
          })}
          profiles={profiles}
          busy={busy}
          canPropose={can.has('strategy.propose')}
          canValidate={can.has('strategy.validate')}
          canApprove={can.has('strategy.approve')}
          onPropose={(draft: ProfileDraft) => void guard(async () => {
            await strategy.propose(draft)
            setProfiles(await strategy.profiles())
          })}
          onUpdate={(profileId, draft) => void guard(async () => {
            await strategy.update(profileId, draft)
            setProfiles(await strategy.profiles())
          })}
          onAct={(profileId, action, note) => void guard(async () => {
            await strategy.act(profileId, action, note)
            setProfiles(await strategy.profiles())
          })}
        />
      )}

      {section === 'insight' && (
        <InsightScreen
          agency={marca?.agencyName || connection.agencyId} user={connection.user}
          loaded={loaded.has('insight')} problem={failures.get('insight') ?? null}
          lang={lang}
          shifts={insightShifts}
          review={insightReview}
          trends={trends}
          busy={busy}
          demo={demo}
          canRebuild={can.has('reports.rebuild')}
          rebuilt={rebuilt}
          onOpen={(date, code) => void guard(async () => {
            setInsightReview(await insight.review(date, code))
          })}
          onRebuild={() => void guard(async () => {
            setRebuilt(await insight.rebuild())
            setInsightShifts(await insight.shifts())
            setTrends(await insight.trends())
            setInsightReview(null)
          })}
          onExport={() => void guard(async () => {
            // La hoja se pide con el token y se entrega al navegador como archivo: un enlace
            // suelto no lleva la sesión, y sin ella esto sería el desempeño de una agencia
            // abierto a quien tenga la dirección.
            const blob = await insight.csv()
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `protect-desempeno-${agencyToday()}.csv`
            a.click()
            URL.revokeObjectURL(url)
          })}
        />
      )}

      {section === 'admin' && (
        <AdminScreen
          districts={districts}
          onUploadDistricts={(geoJson, validFrom) => adminApi.saveDistricts(geoJson, validFrom)}
          districtVersions={adminApi.districtVersions}
          zoneVersions={adminApi.zoneVersions}
          onDistrictsLoaded={() => void guard(async () => setDistricts(await adminApi.districts()))}
          zones={zones}
          onUploadZones={(geoJson, validFrom) => adminApi.savePatrolZones(geoJson, validFrom)}
          onZonesLoaded={() => void guard(async () => setZones(await adminApi.patrolZones()))}
          shiftTemplates={shiftTemplates}
          onSaveShiftTemplates={(t) => guard(async () => {
            const guardadas = await adminApi.saveShiftTemplates(t)
            setShiftTemplates(guardadas)
            setAgencyShifts(guardadas)
          })}
          loaded={loaded.has('admin')} problem={failures.get('admin') ?? null}
          lang={lang}
          devices={devices}
          audit={audit}
          license={license}
          provider={provider?.authority ?? null}
          demo={demo}
          storage={storage}
          mfa={mfa}
          deadLetters={deadLetters}
          deadLetterItems={deadLetterItems}
          onDiscardDeadLetter={can.has('agency.configure') ? async (id, reason) => {
            let ok = false
            await guard(async () => { await adminApi.discardDeadLetter(id, reason); ok = true; await loadAdmin() })
            return ok
          } : undefined}
          busy={busy}
          canDevices={can.has('devices.manage')}
          canAudit={can.has('audit.read')}
          auditKind={auditKind}
          onAuditKind={setAuditKind}
          canConfigure={can.has('agency.configure')}
          canReadAgency={can.has('agency.read')}
          canRules={can.has('agency.rules')}
          canLayers={can.has('agency.layers')}
          targets={targets}
          jurisdictions={jurisdictions}
          onSaveTargets={(t2) => void guard(async () => {
            await adminApi.saveStaffingTargets(t2)
            await loadAdmin()
          })}
          onSaveJurisdictions={(m) => void guard(async () => {
            await adminApi.saveJurisdictions(m)
            await loadAdmin()
          })}
          deviation={deviation}
          approval={approval}
          onSaveDeviation={(p) => void guard(async () => {
            await adminApi.saveDeviationPolicy(p)
            await loadAdmin()
          })}
          onSaveApproval={(p) => void guard(async () => {
            await adminApi.saveApprovalPolicy(p)
            await loadAdmin()
          })}
          formation={formation}
          onSaveFormation={(p) => void guard(async () => {
            await adminApi.saveFormationPolicy(p)
            await loadAdmin()
          })}
          onRegister={(deviceId, label) => void guard(async () => {
            await adminApi.registerDevice(deviceId, label)
            await loadAdmin()
          })}
          onRevoke={(deviceId, reason) => void guard(async () => {
            await adminApi.revokeDevice(deviceId, reason)
            await loadAdmin()
          })}
          branding={marca}
          onSaveBranding={(c) => changeBranding(() => adminApi.saveBranding(c))}
          onSaveBadge={(image) => changeBranding(() => adminApi.saveBadge(image))}
          onRemoveBadge={() => changeBranding(() => adminApi.removeBadge())}
        />
      )}

      {section === 'fleet' && (
        <FleetScreen
          onImport={(file) => tactix.importRoster('vehicles', file)}
          onImported={() => void loadResources('fleet')}
          lang={lang}
          date={shiftDate}
          code={shiftCode}
          onShift={(d, c) => { setShiftDate(d); setShiftCode(c) }}
          vehicles={vehicles}
          availability={availability}
          busy={busy}
          history={fleetHistory}
          onHistory={(vehicleId) => void guard(async () => {
            setFleetHistory(vehicleId === null ? null : await tactix.vehicleHistory(vehicleId))
          })}
          onStatus={(vehicleId, status) => void guard(async () => {
            await tactix.vehicleStatus(vehicleId, status)
            await loadResources('fleet')
          })}
          onPublish={(entries) => guard(async () => {
            await tactix.publishAvailability(shiftDate, shiftCode, 'vehicle', entries)
            await loadResources('fleet')
          })}
        />
      )}

      {section === 'hr' && (
        <HrScreen
          onImport={(file) => tactix.importRoster('officers', file)}
          onImported={() => void loadResources('hr')}
          lang={lang}
          date={shiftDate}
          code={shiftCode}
          onShift={(d, c) => { setShiftDate(d); setShiftCode(c) }}
          officers={officers}
          availability={availability}
          busy={busy}
          onActive={(officerId, isActive) => void guard(async () => {
            await tactix.officerActive(officerId, isActive)
            await loadResources('hr')
          })}
          onPublish={(entries) => guard(async () => {
            await tactix.publishAvailability(shiftDate, shiftCode, 'officer', entries)
            await loadResources('hr')
          })}
        />
      )}

      {section === 'analyst' && (
        <AnalystScreen
          layers={can.has('agency.layers') ? (
            <>
              <GeoAreaEditor
                lang={lang} prefix="districts" areas={districts} busy={busy}
                onUpload={(geoJson, validFrom) => adminApi.saveDistricts(geoJson, validFrom)}
                onLoaded={() => void guard(async () => setDistricts(await adminApi.districts()))}
                loadVersions={adminApi.districtVersions}
              />
              <GeoAreaEditor
                lang={lang} prefix="zones" areas={zones} busy={busy}
                onUpload={(geoJson, validFrom) => adminApi.savePatrolZones(geoJson, validFrom)}
                onLoaded={() => void guard(async () => setZones(await adminApi.patrolZones()))}
                loadVersions={adminApi.zoneVersions}
              />
            </>
          ) : undefined}
          mapBackground={paleta['--bg']}
          base={mapBase}
          area={agencyArea}
          loaded={loaded.has('analyst')} problem={failures.get('analyst') ?? null}
          lang={lang}
          dark={dark}
          sets={sets}
          detail={setDetail}
          busy={busy}
          canCalculate={can.has('hotspots.calculate')}
          canPublish={can.has('hotspots.publish')}
          sources={sources}
          ingestions={ingestions}
          quality={quality}
          incidents={historical}
          onExplore={(filter: IncidentFilter) => void guard(async () => {
            setHistorical(await atlas.incidents(filter))
          })}
          onIngest={(source, since, until) => void guard(async () => {
            await atlas.ingest(source, since, until)
            setIngestions(await atlas.ingestions())
          })}
          onOpen={(setId) => void guard(async () => setSetDetail(await atlas.detail(setId)))}
          changes={setChanges}
          onCompare={(setId) => void guard(async () => {
            setSetChanges(setId === null ? null : await atlas.changes(setId))
          })}
          onCalculate={(r) => void guard(async () => {
            const creado = await atlas.calculate(r)
            setSetDetail(creado)
            setSetChanges(null)
            setSets(await atlas.sets())
          })}
          onPublish={(setId) => void guard(async () => {
            await atlas.publish(setId)
            setSets(await atlas.sets())
            if (setDetail?.set.setId === setId) setSetDetail(await atlas.detail(setId))
          })}
        />
      )}
    </div>
  )
}

/**
 * Dónde ocurrió cada alerta abierta: en el punto de la asignación si la hay (llegada tardía,
 * presencia corta, fuera de zona) y, si no, en la última posición de la unidad (sin conexión).
 * Una alerta sin ninguno de los dos no va al mapa, y la cola lo dice.
 */
export function alertPointsOf(alerts: Alert[], assignments: AssignmentView[], units: UnitView[]): AlertPoint[] {
  const byAssignment = new Map(assignments.map((a) => [a.assignmentId, a]))
  const byUnit = new Map(units.map((u) => [u.patrolUnitId, u]))
  return alerts
    .filter((a) => a.status !== 'Resolved' && a.status !== 'Closed')
    .map((a) => {
      const place = (a.assignmentId ? byAssignment.get(a.assignmentId) : undefined) ?? byUnit.get(a.patrolUnitId)
      if (!place || place.latitude === null || place.longitude === null) return null
      return {
        alertId: a.alertId,
        patrolUnitId: a.patrolUnitId,
        callSign: a.callSign,
        severity: a.severity,
        latitude: place.latitude,
        longitude: place.longitude,
      }
    })
    .filter((p): p is AlertPoint => p !== null)
}

/** Un aviso del canal actualiza lo que ya hay; lo que no venga en el aviso se conserva. */
export function mergeUnit(units: UnitView[], change: Partial<UnitView>): UnitView[] {
  if (!change.patrolUnitId) return units
  const index = units.findIndex((u) => u.patrolUnitId === change.patrolUnitId)
  if (index < 0) return units
  const next = units.slice()
  next[index] = { ...next[index]!, ...change }
  return next
}

export function mergeAlert(alerts: Alert[], change: Alert): Alert[] {
  if (!change?.alertId) return alerts
  const index = alerts.findIndex((a) => a.alertId === change.alertId)
  if (index < 0) return [change, ...alerts]
  const next = alerts.slice()
  next[index] = change
  return next
}
